import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Upload, Loader2 } from 'lucide-react';
import { parseM3U8 } from '../../utils/m3u8Parser';
import { setError, appendChannels, setLoading } from '../../store/slices/playlistSlice';
import { useTranslation } from 'react-i18next';
import { useFirestore } from '../../hooks/useFirestore';
import { RootState } from '../../store';
import logger from '../../utils/logger';
import { usePlaylistDetection } from '../../contexts/PlaylistDetectionContext';

interface PlaylistImportProps {
  onImportSuccess?: () => void;
}

export const PlaylistImport: React.FC<PlaylistImportProps> = ({ onImportSuccess }) => {
  logger.debug('[PlaylistImport] Component rendering');
  
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { savePlaylist } = useFirestore();
  const { user } = useSelector((state: RootState) => state.auth);
  const { channels, isLoading } = useSelector((state: RootState) => state.playlist);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const { isPlaylistDetected } = usePlaylistDetection();

  useEffect(() => {
    logger.debug('[PlaylistImport] isPlaylistDetected changed:', isPlaylistDetected);
    setIsHighlighted(isPlaylistDetected);
  }, [isPlaylistDetected]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.m3u8') && !file.name.toLowerCase().endsWith('.m3u')) {
      dispatch(setError({ message: t('errors.invalidPlaylistFile', 'Please provide a valid M3U8 playlist file'), type: 'error' }));
      return;
    }

    try {
      dispatch(setLoading(true));
      logger.debug('File import started:', file.name);
      
      const content = await file.text();
      logger.debug('Parsing M3U8 content, content length:', content.length);
      const parsedChannels = parseM3U8(content);
      logger.debug('Parsed channels from file:', parsedChannels.length);
      
      if (parsedChannels.length === 0) {
        dispatch(setError({ message: t('errors.noChannels', 'No channels found in the playlist'), type: 'error' }));
        dispatch(setLoading(false));
        return;
      }

      // Count HTTP and HTTPS channels
      const httpsChannels = parsedChannels.filter((channel: any) => {
        try {
          const urlObj = new URL(channel.url);
          return urlObj.protocol === 'https:';
        } catch {
          return false;
        }
      });
      const httpChannels = parsedChannels.filter((channel: any) => {
        try {
          const urlObj = new URL(channel.url);
          return urlObj.protocol === 'http:';
        } catch {
          return false;
        }
      });

      // Create a copy of existing channels to ensure we have the right data for saving
      const existingChannels = [...channels];
      logger.debug('Current channels in Redux state before update:', existingChannels.length);

      // Add all channels to store
      dispatch(appendChannels(parsedChannels));
      logger.debug('Dispatched appendChannels action, channels to add:', parsedChannels.length);

      // Auto-save for both authenticated and non-authenticated users
      if (user?.uid) {
        setIsAutoSaving(true);
        try {
          logger.debug('Starting auto-save process for file import');
          
          // Wait for state to update - ensure state propagation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Calculate what the new state should be (for logging)
          const expectedChannelCount = existingChannels.length + parsedChannels.length;
          logger.debug('Expected channel count after update:', expectedChannelCount);
          logger.debug('Actual channel count in Redux before save:', channels.length);
          
          // Save all channels including the newly imported ones
          if (channels.length < expectedChannelCount) {
            logger.debug('Redux state not yet updated, using direct save with combined channels');
            const combinedChannels = [...existingChannels, ...parsedChannels];
            await savePlaylist(user.uid, combinedChannels);
          } else {
            logger.debug('Using direct save with updated Redux state');
            await savePlaylist(user.uid, channels);
          }
          
          logger.debug('Auto-save completed successfully for file import');
          
          dispatch(setError({ 
            message: t('playlist.importAndSaveSuccessWithHttp', 'Successfully imported and saved {{total}} channels ({{https}} HTTPS, {{http}} HTTP)', { 
              total: parsedChannels.length,
              https: httpsChannels.length,
              http: httpChannels.length
            }),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist from file import:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        } finally {
          setIsAutoSaving(false);
        }
      } else {
        dispatch(setError({ 
          message: t('playlist.importSuccessWithHttp', 'Successfully imported {{total}} channels ({{https}} HTTPS, {{http}} HTTP)', { 
            total: parsedChannels.length,
            https: httpsChannels.length,
            http: httpChannels.length
          }),
          type: 'success' 
        }));
      }

      localStorage.setItem('activePlaylistTab', 'channels');
      onImportSuccess?.();
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      logger.error('Playlist import error:', error);
      dispatch(setError({ message: t('errors.parseError', 'Failed to parse playlist file'), type: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    
    const file = e.dataTransfer.files[0];
    if (!file || (!file.name.toLowerCase().endsWith('.m3u8') && !file.name.toLowerCase().endsWith('.m3u'))) {
      dispatch(setError({ message: t('errors.invalidPlaylistFile', 'Please provide a valid M3U8 playlist file'), type: 'error' }));
      return;
    }

    try {
      dispatch(setLoading(true));
      logger.debug('File import started (drag & drop):', file.name);
      
      const content = await file.text();
      logger.debug('Parsing M3U8 content, content length:', content.length);
      const parsedChannels = parseM3U8(content);
      logger.debug('Parsed channels from file:', parsedChannels.length);
      
      if (parsedChannels.length === 0) {
        dispatch(setError({ message: t('errors.noChannels', 'No channels found in the playlist'), type: 'error' }));
        dispatch(setLoading(false));
        return;
      }

      // Count HTTP and HTTPS channels
      const httpsChannels = parsedChannels.filter((channel: any) => {
        try {
          const urlObj = new URL(channel.url);
          return urlObj.protocol === 'https:';
        } catch {
          return false;
        }
      });
      const httpChannels = parsedChannels.filter((channel: any) => {
        try {
          const urlObj = new URL(channel.url);
          return urlObj.protocol === 'http:';
        } catch {
          return false;
        }
      });

      // Create a copy of existing channels to ensure we have the right data for saving
      const existingChannels = [...channels];
      logger.debug('Current channels in Redux state before update:', existingChannels.length);

      // Add all channels to store
      dispatch(appendChannels(parsedChannels));
      logger.debug('Dispatched appendChannels action, channels to add:', parsedChannels.length);

      // Auto-save for both authenticated and non-authenticated users
      if (user?.uid) {
        setIsAutoSaving(true);
        try {
          logger.debug('Starting auto-save process for file import (drag & drop)');
          
          // Wait for state to update - ensure state propagation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Calculate what the new state should be (for logging)
          const expectedChannelCount = existingChannels.length + parsedChannels.length;
          logger.debug('Expected channel count after update:', expectedChannelCount);
          logger.debug('Actual channel count in Redux before save:', channels.length);
          
          // Save all channels including the newly imported ones
          if (channels.length < expectedChannelCount) {
            logger.debug('Redux state not yet updated, using direct save with combined channels');
            const combinedChannels = [...existingChannels, ...parsedChannels];
            await savePlaylist(user.uid, combinedChannels);
          } else {
            logger.debug('Using direct save with updated Redux state');
            await savePlaylist(user.uid, channels);
          }
          
          logger.debug('Auto-save completed successfully for file import (drag & drop)');
          
          dispatch(setError({ 
            message: t('playlist.importAndSaveSuccessWithHttp', 'Successfully imported and saved {{total}} channels ({{https}} HTTPS, {{http}} HTTP)', { 
              total: parsedChannels.length,
              https: httpsChannels.length,
              http: httpChannels.length
            }),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist from file import (drag & drop):', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        } finally {
          setIsAutoSaving(false);
        }
      } else {
        dispatch(setError({ 
          message: t('playlist.importSuccessWithHttp', 'Successfully imported {{total}} channels ({{https}} HTTPS, {{http}} HTTP)', { 
            total: parsedChannels.length,
            https: httpsChannels.length,
            http: httpChannels.length
          }),
          type: 'success' 
        }));
      }
      
      onImportSuccess?.();
    } catch (error) {
      logger.error('Playlist import error (drag & drop):', error);
      dispatch(setError({ message: t('errors.parseError', 'Failed to parse playlist file'), type: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="w-full">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".m3u8,.m3u"
        className="hidden"
      />

      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center cursor-pointer 
          hover:border-primary dark:hover:border-primary transition-colors duration-200
          ${isHighlighted ? 'border-primary dark:border-primary animate-pulse' : ''}`}
      >
        {isLoading || isAutoSaving ? (
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
            <span className="text-gray-500">
              {isAutoSaving ? t('common.saving', 'Saving...') : t('common.loading', 'Loading...')}
            </span>
          </div>
        ) : (
          <>
            <Upload className={`mx-auto h-12 w-12 text-gray-400 ${isHighlighted ? 'text-primary' : ''}`} />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('playlist.dragDropText', 'Click to upload or drag and drop your M3U8 playlist file')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
